import { getMyLeaveEmployments, validateLeaveRequest, type LeaveEmployment } from '@/lib/hrApi';
import { toDisplayDate } from '@/lib/orgFormat';
import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Send, X } from 'lucide-react';
import { useC, type C as ColorSet } from '@/features/shared/theme/kaziColors';
import { getLeaveTypes, getCoverageRequirement, type CoverageRequirement } from '@/lib/hrApi';
import { ShieldCheck, UserCheck } from 'lucide-react';
import type { LeavePrefill } from '@/features/delegation/leaveBridge';
import { HandoverMessageField, useHandoverNote } from '@/features/delegation/HandoverMessageField';
import { coverageSubmitState, existingDelegationOptions, preferredExisting } from '@/features/delegation/handover';
import { ExistingDelegationPicker, type ExistingLoadState } from '@/features/delegation/ExistingDelegationPicker';
import { getDelegations, type Delegation } from '@/services/rbacService';

/** The delegation attached to this draft (from the delegation page hand-off). */
export interface CoverageDraft {
  delegationId: string;
  delegateName?: string | null;
  status?: string | null; // delegation status (active | pending_acceptance | …)
  ccCount?: number;
}

interface RequestTimeOffFormProps {
  /** Resolves to a refusal reason (string) when the request was not accepted;
   *  the form then says it where the person is looking, not only in a toast. */
  onSubmit: (data: any) => void | string | Promise<void | string>;
  onCancel: () => void;
  /** Delegation ↔ Leave: values carried in from a delegation or a kept draft. */
  prefill?: LeavePrefill & { type?: string; notes?: string; dayType?: string };
  /** Tenant policy evaluated for the caller (null while loading / unavailable). */
  coverageRequirement?: CoverageRequirement | null;
  /** Delegation attached to the draft, if any. */
  coverage?: CoverageDraft | null;
  /** Open the delegation flow with the current draft (kept for the return trip). */
  onSetUpDelegation?: (draft: { type: string; startDate: string; endDate: string; reason: string; notes: string; dayType: string }) => void;
}

/** TODAY where the person is, not in UTC.
 *
 *  toISOString() is UTC: for a viewer west of UTC in the evening it returns
 *  TOMORROW, so `min` blocked today and "start date cannot be in the past"
 *  rejected a same-day request. East of UTC before 03:00 it returned yesterday.
 *  A leave date is a civil date; it must come from the local clock. */
const shortDay = (iso: string) => toDisplayDate(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

const todayIso = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const RequestTimeOffForm: React.FC<RequestTimeOffFormProps> = ({ onSubmit, onCancel, prefill, coverageRequirement, coverage: coverageProp, onSetUpDelegation }) => {
  const { t } = useTranslation(['timeoff']);
  const C = useC();
  const [formData, setFormData] = useState({
    type: prefill?.type ?? '',
    // Default to today. Empty date inputs render as an mm/dd/yyyy placeholder
    // and every request starts with the same two clicks; the overwhelmingly
    // common case is leave starting today or later, so today is the useful
    // floor rather than a blank.
    startDate: prefill?.startDate ?? todayIso(),
    // Matches the start date, and KEEPS matching it until the user extends the
    // request — see setStartDate. A fixed "today" here is what produced a
    // 0-day request the moment someone picked a future start.
    endDate: prefill?.endDate ?? prefill?.startDate ?? todayIso(),
    reason: prefill?.reason ?? '',
    notes: prefill?.notes ?? '',
  });
  // "No delegation needed" attestation. The statement is a CONSTANT here and
  // is sent with the request so the stored record holds the exact words the
  // person agreed to — re-wording this string later must not retroactively
  // change what someone already signed.
  const ATTESTATION_STATEMENT = t('timeoff:attestation_statement', {
    defaultValue: "I confirm there's nothing requiring coverage during my leave.",
  });
  const [attested, setAttested] = useState(false);
  const [signedName, setSignedName] = useState('');

  // Half-day leave (Phase 4): only meaningful when the request is one day.
  const [dayType, setDayType] = useState<'full' | 'half_morning' | 'half_afternoon'>('full');
  // Multi-entity G5: someone employed by more than one company chooses which
  // employment the leave is taken from (each has its own balance).
  const [employments, setEmployments] = useState<LeaveEmployment[]>([]);
  // R2: a day off can cover several employments at once ("Apply to").
  const [engagementIds, setEngagementIds] = useState<string[]>([]);
  const engagementId = engagementIds[0] ?? '';
  React.useEffect(() => {
    let cancelled = false;
    getMyLeaveEmployments()
      .then((r) => {
        if (cancelled || !r.per_employment) return;
        setEmployments(r.employments);
        const first = (r.employments.find((e) => e.is_primary) ?? r.employments[0])?.engagement_id;
        setEngagementIds(first ? [first] : []);
      })
      .catch(() => undefined); // not available → the single ledger, as before
    return () => { cancelled = true; };
  }, []);
  const singleDay = !!formData.startDate && (!formData.endDate || formData.endDate === formData.startDate);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  // ⚠️ NEVER A SILENT SUBMIT (DEF-0mvpyq10b001-b77ec6). Every refusal (a policy
  // rule, a missing field, the server) is said here, next to the button, and
  // as a toast. Before, a required-but-missing delegate DISABLED Submit with
  // only a hover tooltip for a reason: four clicks, nothing happened.
  const [refusal, setRefusal] = useState<string | null>(null);
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);

  // Fallback if the catalog can't be fetched (keeps the form usable offline).
  const FALLBACK_TYPES = [
    { value: 'ANNUAL', label: t('timeoff:annual_leave', { defaultValue: 'Annual Leave' }), color: '#F28A3A' },
    { value: 'SICK', label: t('timeoff:sick_leave', { defaultValue: 'Sick Leave' }), color: '#F6B26B' },
    { value: 'WFH', label: t('timeoff:work_from_home', { defaultValue: 'Work From Home' }), color: '#D97706' },
  ];
  const TYPE_COLORS = ['#F28A3A', '#3B82F6', '#22C55E', '#8B5CF6', '#EF4444', '#F6B26B', '#0EA5E9', '#D97706', '#EC4899'];

  // Data-driven leave types: the tenant's configured catalog (value = type_code).
  const [timeOffTypes, setTimeOffTypes] = useState(FALLBACK_TYPES);
  useEffect(() => {
    let active = true;
    getLeaveTypes()
      .then((types) => {
        if (!active || !types?.length) return;
        setTimeOffTypes(types.map((ty, i) => ({ value: ty.type_code, label: ty.name, color: TYPE_COLORS[i % TYPE_COLORS.length] })));
      })
      .catch(() => { /* keep fallback */ });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectedType = timeOffTypes.find((x) => x.value === formData.type);

  const set = (k: string, v: string) => setFormData((p) => ({ ...p, [k]: v }));

  /** Moving the start date drags the end date with it.
   *
   *  Both fields default to today, so picking a future start left the end date
   *  BEHIND it — an invalid range that the server correctly costed at
   *  "0 Days", on a form that gave no hint which field was wrong. The end date
   *  only stays put when the user has deliberately moved it to a later day;
   *  anything earlier than the start is not a preference, it is a leftover. */
  const setStartDate = (v: string) =>
    setFormData((p) => ({
      ...p,
      startDate: v,
      endDate: !p.endDate || p.endDate < v ? v : p.endDate,
    }));

  const validate = () => {
    const e: { [key: string]: string } = {};
    if (!formData.type) e.type = t('timeoff:form_select_leave_type', { defaultValue: 'Select a leave type' });
    if (!formData.startDate) e.startDate = t('timeoff:form_select_start_date', { defaultValue: 'Select a start date' });
    if (formData.startDate && formData.startDate < todayIso()) e.startDate = t('timeoff:error_start_date_past', { defaultValue: 'Start date cannot be in the past' });
    if (formData.endDate && formData.startDate && formData.endDate < formData.startDate) e.endDate = t('timeoff:error_end_date_before_start', { defaultValue: 'End date cannot be before start date' });
    if (!formData.reason.trim()) e.reason = t('timeoff:form_provide_reason', { defaultValue: 'Please provide a reason' });
    setErrors(e);
    return e;
  };

  // Delegation ↔ Leave: the POLICY decides whether coverage blocks submission
  // (the server enforces the same rule — this only explains it up front).
  //
  // Re-evaluated PER LEAVE TYPE, because the answer differs by type: Work From
  // Home is exempt (the person is still working, so there is nothing to hand
  // over). The prop is the tenant's general answer, fetched before a type was
  // chosen; once one is, the type-specific answer replaces it. Both come from
  // the same server evaluation that gates submission, so the form cannot
  // enable Submit on a request the backend will refuse.
  const [typeRequirement, setTypeRequirement] = useState<CoverageRequirement | null>(null);
  useEffect(() => {
    if (!formData.type) { setTypeRequirement(null); return; }
    let active = true;
    getCoverageRequirement(formData.type)
      .then((r) => { if (active) setTypeRequirement(r); })
      // A failed lookup falls back to the tenant's general answer rather than
      // to "not required" — never relax a control because a call blipped.
      .catch(() => { if (active) setTypeRequirement(null); });
    return () => { active = false; };
  }, [formData.type]);
  const requirement = typeRequirement ?? coverageRequirement ?? null;

  // ── Use an EXISTING delegation (prod 2026-10-22) ──────────────────────
  // The form used to know only a delegation handed over from the delegation
  // page, so one set up there FIRST — and accepted — was invisible: Submit
  // stayed disabled and a duplicate followed. It now lists the requester's
  // own delegations (granted BY them), and reads the chosen one's LIVE status
  // from that list rather than the hand-off snapshot. The server re-checks
  // ownership, dates and links on submit.
  const [mine, setMine] = useState<Delegation[] | null>(null);
  const [mineState, setMineState] = useState<ExistingLoadState>('loading');
  const [mineNonce, setMineNonce] = useState(0);
  useEffect(() => {
    let active = true;
    setMineState('loading');
    getDelegations()
      .then((m) => { if (active) { setMine(m.asDelegator); setMineState('ready'); } })
      .catch(() => { if (active) setMineState('error'); });
    return () => { active = false; };
  }, [mineNonce]);
  const [chosenId, setChosenId] = useState<string | null>(coverageProp?.delegationId ?? null);
  // Once the person picks (or clears) a delegation, never second-guess them.
  const [userChose, setUserChose] = useState(!!coverageProp?.delegationId);
  useEffect(() => {
    if (coverageProp?.delegationId) { setChosenId(coverageProp.delegationId); setUserChose(true); }
  }, [coverageProp?.delegationId]);
  const existing = React.useMemo(
    () => existingDelegationOptions(mine, formData.startDate, formData.endDate || formData.startDate),
    [mine, formData.startDate, formData.endDate],
  );
  // No duplicates: where a delegate is required and one of theirs already
  // covers the dates, preselect it (accepted first) instead of asking for a
  // new one. Optional policies list them without attaching anything unasked.
  useEffect(() => {
    if (userChose || chosenId || mineState !== 'ready' || !requirement?.required) return;
    const best = preferredExisting(existing.eligible);
    if (best) setChosenId(best.delegation.id);
  }, [userChose, chosenId, mineState, requirement?.required, existing]);
  const chooseExisting = (id: string | null) => { setUserChose(true); setChosenId(id); };
  const chosenRow = chosenId ? (mine ?? []).find((d) => d.id === chosenId) ?? null : null;
  const fromProp = coverageProp && coverageProp.delegationId === chosenId ? coverageProp : null;
  const coverage: CoverageDraft | null = chosenId
    ? {
        delegationId: chosenId,
        delegateName: chosenRow?.delegateName ?? fromProp?.delegateName ?? null,
        status: chosenRow?.status ?? fromProp?.status ?? null,
        ccCount: chosenRow ? (chosenRow.ccUserIds ?? []).length : fromProp?.ccCount,
      }
    : null;
  // The chosen one no longer fits (dates moved, or it's busy elsewhere): say
  // why and hold Submit — the server would refuse it anyway.
  const chosenIneligible = chosenId ? existing.ineligible.find((o) => o.delegation.id === chosenId) ?? null : null;

  const coverageConfirmed = !!coverage && ['active', 'scheduled'].includes(String(coverage.status ?? ''));
  const coverageDead = !!coverage && ['declined', 'revoked', 'rejected', 'expired'].includes(String(coverage.status ?? ''));
  const showCoverage = !!requirement?.required || !!coverage || !!onSetUpDelegation || existing.eligible.length > 0;
  // A signed attestation is the alternative to naming a delegate, for someone
  // with nothing to hand over. Both a tick AND a signature: a lone checkbox is
  // the silent opt-out this deliberately is not.
  const attestationSigned = attested && signedName.trim().length > 0;
  // Mirrors leave-service coverage_gate('submit'): a delegate who has not
  // accepted yet NEVER blocks Submit (owner decision 2026-10-22) — the request
  // goes in with cover pending, and the approver sees it.
  const cover = coverageSubmitState({
    required: requirement?.required,
    confirmation: requirement?.confirmation,
    status: coverage?.status,
    hasCoverage: !!coverage,
    startDate: formData.startDate,
  });
  const blockedBySubmissionRule = !!chosenIneligible || (!attestationSigned && cover.blocked);
  const coverageStatusLabel = !coverage
    ? t('timeoff:coverage_not_set', { defaultValue: 'Not set up yet' })
    : coverageConfirmed
      ? t('timeoff:coverage_status_confirmed', { defaultValue: 'Coverage confirmed' })
      : coverage.status === 'declined'
        ? t('timeoff:coverage_status_declined', { defaultValue: 'Coverage declined' })
        : coverageDead
          ? t('timeoff:coverage_status_cancelled', { defaultValue: 'Coverage cancelled' })
          : t('timeoff:coverage_status_pending', { defaultValue: 'Coverage pending' });

  // Handover message to the delegate — saved ON the delegation (PATCH), so it
  // reaches the delegate whether or not this leave is ever submitted. Locked
  // once coverage starts (the delegation window, buffers included).
  const handover = useHandoverNote(coverage?.delegationId ?? null);

  // Why Submit can't go ahead, in words (null = it can).
  const blockedReason: string | null = chosenIneligible
    ? (chosenIneligible.reason === 'linked_elsewhere'
      ? t('timeoff:existing_chosen_linked', { defaultValue: 'This delegation already covers another time-off request. Choose another one or create a new delegation.' })
      : t('timeoff:existing_chosen_dates', { first: shortDay(chosenIneligible.firstDay), last: shortDay(chosenIneligible.lastDay), defaultValue: "This delegation ({{first}} – {{last}}) doesn't cover every day of your leave. Choose another one or create a new delegation." }))
    : blockedBySubmissionRule
      ? (cover.state === 'dead'
        ? t('timeoff:submit_blocked_dead', { defaultValue: 'Your delegation for this leave was declined or cancelled. Choose another delegate or create a new delegation, then submit.' })
        : t('timeoff:submit_blocked_missing', { defaultValue: "Company policy requires you to name a delegate for this leave. Name one under Coverage & delegation (they don't need to have accepted yet), or sign the statement there if you have nothing to hand over." }))
      : null;
  const refuse = (why: string) => {
    setRefusal(why);
    toast.error(why, { duration: 8000 });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting.current) return;                  // one request per press, however many clicks
    setRefusal(null);
    if (blockedReason) { refuse(blockedReason); return; }
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length) {
      refuse(t('timeoff:submit_fix_fields', { list: Object.values(fieldErrors).join(' · '), defaultValue: 'Not sent yet: {{list}}' }));
      return;
    }
    submitting.current = true;
    setBusy(true);
    try {
      // An unsaved message is saved first; if that fails the request is NOT
      // sent, so the person never loses what they wrote (the error is toasted).
      if (handover.dirty && !handover.lock && !(await handover.save())) {
        setRefusal(t('timeoff:submit_handover_unsaved', { defaultValue: "Your message to your delegate couldn't be saved, so the request wasn't sent. Try again." }));
        return;
      }
      const answer = await onSubmit({
      ...formData,
      dayType: singleDay ? dayType : 'full',
      engagementId: engagementId || undefined,
      engagementIds: engagementIds.length > 1 ? engagementIds : undefined,
      delegationId: coverage?.delegationId ?? null,
      // signed_at is stamped server-side — a signature the signer dates is
      // not evidence of anything.
      coverageAttestation: attestationSigned
        ? { statement: ATTESTATION_STATEMENT, signedName: signedName.trim() }
        : null,
      });
      // The page already toasted a refusal; say it here too.
      if (typeof answer === 'string' && answer) setRefusal(answer);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: C.charcoalMid, marginBottom: 6, display: 'block' };
  const field: React.CSSProperties = { width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.white, color: C.charcoal, fontSize: 13, fontFamily: 'inherit', outline: 'none' };
  const errText: React.CSSProperties = { fontSize: 11, color: C.red, marginTop: 4 };
  // Phase 0 (F6): the day count comes from the SERVER's canonical working-day
  // engine (holidays + tenant rest days), never client calendar arithmetic.
  const [serverDays, setServerDays] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (!formData.type || !formData.startDate) { setServerDays(null); return; }
    let cancelled = false;
    validateLeaveRequest({
      type: formData.type, startDate: formData.startDate,
      endDate: formData.endDate || undefined,
      dayType: singleDay ? dayType : 'full',
      engagementId: engagementId || undefined,
    }).then((v) => { if (!cancelled) setServerDays(v.days_requested); })
      .catch(() => { if (!cancelled) setServerDays(null); });
    return () => { cancelled = true; };
  }, [formData.type, formData.startDate, formData.endDate, singleDay, dayType, engagementId]);
  // NOT `?? 0`. Zero is a real answer — a span that is entirely weekends or
  // public holidays genuinely costs no days — but "we have not been told yet"
  // is not zero, and rendering it as "0 days" made an in-flight calculation
  // look like a rejected request.
  const days = serverDays;

  return (
    <div style={{ padding: 28, maxWidth: 640, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: C.charcoal, marginBottom: 6 }}>{t('timeoff:form_title', { defaultValue: 'Request Time Off' })}</h2>
      <p style={{ fontSize: 13, color: C.charcoalLight, marginBottom: 24 }}>Fill out the form below to submit your time off request. All requests require manager approval.</p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Type */}
          <div>
            <label style={label}>{t('timeoff:form_leave_type', { defaultValue: 'Leave type' })}</label>
            <select value={formData.type} onChange={(e) => set('type', e.target.value)} style={{ ...field, borderColor: errors.type ? C.red : C.border }}>
              <option value="">—</option>
              {timeOffTypes.map((ty) => <option key={ty.value} value={ty.value}>{ty.label}</option>)}
            </select>
            {errors.type ? <p style={errText}>{errors.type}</p> : null}
          </div>

          {/* Employment (G5/R2) — only for someone employed by more than one company */}
          {employments.length > 1 ? (
            <div>
              <span style={label}>{t('timeoff:form_apply_to', { defaultValue: 'Apply to' })}</span>
              <div role="group" aria-label={t('timeoff:form_apply_to', { defaultValue: 'Apply to' })}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }} data-testid="leave-employment">
                {employments.map((e) => {
                  const on = engagementIds.includes(e.engagement_id);
                  return (
                    <label key={e.engagement_id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.charcoal }}>
                      <input type="checkbox" checked={on} aria-label={e.company}
                        onChange={() => setEngagementIds((cur) => on
                          ? (cur.length > 1 ? cur.filter((x) => x !== e.engagement_id) : cur)
                          : [...cur, e.engagement_id])} />
                      {e.company}{e.is_primary ? ` (${t('timeoff:primary', { defaultValue: 'primary' })})` : ''}
                    </label>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: C.charcoalSubtle, marginTop: 4 }}>
                {engagementIds.length > 1
                  ? t('timeoff:apply_to_many_hint', { defaultValue: 'One request per employment: each is charged to its own balance and approved by that employment\'s manager. Your calendar shows one day off.' })
                  : t('timeoff:employment_hint', { defaultValue: 'Each employment has its own leave balance.' })}
              </p>
            </div>
          ) : null}

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={label}>{t('timeoff:form_start_date', { defaultValue: 'Start date' })}</label>
              <input type="date" min={todayIso()} value={formData.startDate} onChange={(e) => setStartDate(e.target.value)} style={{ ...field, borderColor: errors.startDate ? C.red : C.border }} />
              {errors.startDate ? <p style={errText}>{errors.startDate}</p> : null}
            </div>
            <div>
              <label style={label}>{t('timeoff:form_end_date', { defaultValue: 'End date' })}</label>
              <input type="date" min={formData.startDate || todayIso()} value={formData.endDate} onChange={(e) => set('endDate', e.target.value)} style={{ ...field, borderColor: errors.endDate ? C.red : C.border }} />
              <p style={{ fontSize: 11, color: errors.endDate ? C.red : C.charcoalSubtle, marginTop: 4 }}>{errors.endDate || 'Leave blank for a single day'}</p>
            </div>
          </div>

          {/* Half-day (Phase 4) — single-day requests only */}
          {singleDay ? (
            <div>
              <label style={label}>{t('timeoff:form_duration', { defaultValue: 'Duration' })}</label>
              <select value={dayType} onChange={(e) => setDayType(e.target.value as any)} style={field}>
                <option value="full">{t('timeoff:full_day', { defaultValue: 'Full day' })}</option>
                <option value="half_morning">{t('timeoff:half_morning', { defaultValue: 'Half day — morning' })}</option>
                <option value="half_afternoon">{t('timeoff:half_afternoon', { defaultValue: 'Half day — afternoon' })}</option>
              </select>
            </div>
          ) : null}

          {/* Duration */}
          {formData.startDate ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: C.orangeLight }}>
              <span style={{ fontSize: 13, color: C.charcoalMid }}>Total duration:</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: selectedType?.color || C.orange, borderRadius: 99, padding: '3px 10px' }}>
                {days === null
                  ? t('timeoff:duration_pending', { defaultValue: 'calculating…' })
                  : `${days} ${days !== 1 ? t('timeoff:days', { defaultValue: 'days' }) : t('timeoff:day', { defaultValue: 'day' })}`}
              </span>
            </div>
          ) : null}

          {/* Reason */}
          <div>
            <label style={label}>{t('timeoff:form_reason', { defaultValue: 'Reason' })}</label>
            <textarea value={formData.reason} onChange={(e) => set('reason', e.target.value)} rows={2} style={{ ...field, resize: 'vertical', borderColor: errors.reason ? C.red : C.border }} />
            <p style={{ fontSize: 11, color: errors.reason ? C.red : C.charcoalSubtle, marginTop: 4 }}>{errors.reason || 'Brief description of why you need time off'}</p>
          </div>

          {/* Notes */}
          <div>
            <label style={label}>{t('timeoff:form_notes', { defaultValue: 'Additional notes' })}</label>
            <textarea value={formData.notes} onChange={(e) => set('notes', e.target.value)} rows={3} style={{ ...field, resize: 'vertical' }} />
          </div>

          {/* Coverage & Delegation (Delegation ↔ Leave) — policy-driven, never hardcoded */}
          {showCoverage && (
            <div style={{ borderRadius: 12, border: `1.5px solid ${blockedBySubmissionRule ? C.orange : C.border}`, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="coverage-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ShieldCheck size={16} color={C.orange} />
                <span style={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}>{t('timeoff:coverage_title', { defaultValue: 'Coverage & delegation' })}</span>
              </div>
              <p style={{ fontSize: 12, color: C.charcoalMid, margin: 0 }}>
                {requirement?.required
                  ? t('timeoff:coverage_required_named', { defaultValue: "Company policy requires you to name a delegate for this leave. They don't need to have accepted before you submit." })
                  : requirement?.typeMode === 'manager'
                    ? t('timeoff:coverage_type_manager', { defaultValue: 'Your manager arranges cover for this type of leave. You can still delegate if you want to.' })
                  : requirement?.exemptTypeCode
                    // Says WHY it is optional here. Without this the employee
                    // cannot tell an exempt type from a company with no policy.
                    ? t('timeoff:coverage_exempt', { defaultValue: "You'll still be working, so no delegation is needed. You can still delegate if you want to." })
                    : t('timeoff:coverage_optional', { defaultValue: "Optionally delegate your responsibilities while you're away." })}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.charcoalMid, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('timeoff:coverage_delegate_label', { defaultValue: 'Delegate' })}</div>
                  <div style={{ fontSize: 13, color: C.charcoal, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <UserCheck size={14} color={C.charcoalMid} />
                    {coverage?.delegateName || (coverage ? t('timeoff:coverage_delegated_to', { defaultValue: 'Delegated to' }) : t('timeoff:coverage_not_set', { defaultValue: 'Not set up yet' }))}
                  </div>
                  <div style={{ fontSize: 11, color: C.charcoalSubtle }}>{t('timeoff:coverage_delegate_hint', { defaultValue: 'Will temporarily receive the selected responsibilities.' })}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.charcoalMid, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('timeoff:coverage_cc_label', { defaultValue: 'CC' })}</div>
                  <div style={{ fontSize: 13, color: C.charcoal, marginTop: 2 }}>{coverage ? t('timeoff:coverage_cc_count', { count: coverage.ccCount ?? 0, defaultValue: '{{count}} copied' }) : '—'}</div>
                  <div style={{ fontSize: 11, color: C.charcoalSubtle }}>{t('timeoff:coverage_cc_hint', { defaultValue: 'Receives visibility and notifications only.' })}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: coverageConfirmed ? '#15803D' : coverageDead ? C.red : C.orange }}>{coverageStatusLabel}</span>
              </div>
              {chosenIneligible && (
                <p className="m-0 text-xs font-semibold text-warning-strong" data-testid="coverage-chosen-ineligible">
                  {chosenIneligible.reason === 'linked_elsewhere'
                    ? t('timeoff:existing_chosen_linked', { defaultValue: 'This delegation already covers another time-off request. Choose another one or create a new delegation.' })
                    : t('timeoff:existing_chosen_dates', { first: shortDay(chosenIneligible.firstDay), last: shortDay(chosenIneligible.lastDay), defaultValue: "This delegation ({{first}} – {{last}}) doesn't cover every day of your leave. Choose another one or create a new delegation." })}
                </p>
              )}
              <ExistingDelegationPicker
                state={mineState}
                eligible={existing.eligible}
                ineligible={existing.ineligible}
                selectedId={chosenId}
                onSelect={chooseExisting}
                onRetry={() => setMineNonce((n) => n + 1)}
                onCreateNew={onSetUpDelegation ? () => onSetUpDelegation({ ...formData, dayType: singleDay ? dayType : 'full' }) : undefined}
                startDate={formData.startDate}
                endDate={formData.endDate || formData.startDate}
                allowNone={!requirement?.required}
              />
              {coverage && <HandoverMessageField controller={handover} delegateName={coverage.delegateName} />}
              {/* The alternative, offered only where a delegate is actually
                  required and none is attached. Shown as a statement someone
                  signs, not a box that makes a warning disappear — the whole
                  point is that the approver later reads a claim a person made. */}
              {requirement?.required && !coverage && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: C.charcoal, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={attested}
                      onChange={(e) => setAttested(e.target.checked)}
                      style={{ marginTop: 2 }}
                    />
                    <span>{ATTESTATION_STATEMENT}</span>
                  </label>
                  {attested && (
                    <div>
                      <label style={{ ...label, marginBottom: 4 }} htmlFor="attestation-name">
                        {t('timeoff:attestation_sign', { defaultValue: 'Type your full name to sign' })}
                      </label>
                      <input
                        id="attestation-name"
                        value={signedName}
                        onChange={(e) => setSignedName(e.target.value)}
                        placeholder={t('timeoff:attestation_name_ph', { defaultValue: 'Your full name' })}
                        style={{ ...field, maxWidth: 320 }}
                      />
                      <p style={{ fontSize: 11, color: C.charcoalSubtle, marginTop: 4 }}>
                        {t('timeoff:attestation_note', {
                          defaultValue: 'Your approver will see this statement, your name and the date.',
                        })}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {coverage && cover.state === 'pending' && (
                <p style={{ fontSize: 12, color: C.charcoalMid, margin: 0 }} data-testid="coverage-pending-note">
                  {t('timeoff:coverage_pending_submit', { name: coverage.delegateName || t('timeoff:coverage_delegate_label', { defaultValue: 'Delegate' }), defaultValue: "You can submit now. {{name}} hasn't accepted yet — your approver will see that cover is pending." })}
                </p>
              )}
            </div>
          )}

          <div style={{ height: 1, background: C.border }} />

          {refusal && (
            <p role="alert" data-testid="submit-refusal" style={{ margin: 0, padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.red}`, color: C.red, fontSize: 13, fontWeight: 600 }}>
              {refusal}
            </p>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onCancel} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 20px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'transparent', color: C.charcoalMid, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <X size={16} /> {t('timeoff:form_cancel', { defaultValue: 'Cancel' })}
            </button>
            {/* aria-disabled, not disabled: a disabled button swallows the click,
                and the click is where the person learns why (blockedReason). */}
            <button type="submit" aria-disabled={blockedBySubmissionRule || busy} aria-busy={busy} title={blockedReason ?? undefined} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 22px', borderRadius: 10, border: 'none', background: selectedType?.color || C.orange, color: '#fff', fontSize: 13, fontWeight: 600, cursor: blockedBySubmissionRule ? 'not-allowed' : busy ? 'progress' : 'pointer', opacity: blockedBySubmissionRule || busy ? 0.55 : 1 }}>
              <Send size={16} /> {t('timeoff:form_submit', { defaultValue: 'Submit Request' })}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RequestTimeOffForm;
